import { useMemo } from 'react';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { PetitionVictoryUpdate } from '#src/app/pages/petitionDetails/shared/types';

/**
 * Converts a truncated victory date string (e.g. 2021-01-01) from the database into an ISO date
 * string formatted to match the truncated string, working around timezone issues: JS doesn't
 * handle a truncated date very well—in a time zone earlier than GMT, this would otherwise be
 * parsed to e.g. 2021-01-01 00:00:00 GMT, which could be 2020-12-31 in the browser/server's
 * time zone. This uses the date parts directly to build the ISO string instead.
 */
function fixVictoryDateTimeZone(victoryDate: string): string | undefined {
	const [year, month, day] = victoryDate.split('-').map((strPart) => Number(strPart));
	if (year === undefined || month === undefined || day === undefined) return undefined;
	// Subtract 1 from month because months are zero-indexed
	const date = new Date(year, month - 1, day);
	return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export function useVictoryUpdate(): PetitionVictoryUpdate | undefined {
	const {
		data: {
			petition: { id, status, victoryDate, victoryDateLocalized, victoryDescription },
		},
	} = usePetitionDetailsPageData();

	return useMemo(() => {
		// We should only show the victory update if the petition is in a victory status and
		// has the date and description fields required to render the update.
		// Some petitions can have their victories confirmed and later be moved to a non-victory
		// state—in this case, the victory date and description can remain on the petition,
		// but we'll want to hide the update to be consistent with the petition status header.
		if (status !== 'VICTORY' || !victoryDate || !victoryDateLocalized || !victoryDescription) return undefined;

		const createdAt = fixVictoryDateTimeZone(victoryDate);
		if (!createdAt) return undefined;

		return {
			id: `__petition_${id}_victory_update_fake_id__`,
			type: 'victory' as const,
			createdAt,
			createdAtLocalized: victoryDateLocalized,
			description: victoryDescription,
		};
	}, [status, victoryDate, victoryDateLocalized, victoryDescription, id]);
}
